import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { 
    Table, 
    Button, 
    Badge, 
    Form, 
    Alert, 
    Spinner, 
    Card,
    Tab,
    Tabs,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faUserShield, 
    faUser, 
    faUserMinus, 
    faSearch,
    faFilter,
    faSync,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";

const CommunityUsersAssociations = () => {
    const [associations, setAssociations] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { csrfToken } = useAuth();

    // Filters
    const [selectedCommunity, setSelectedCommunity] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchAssociations = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // First, fetch all users
            const usersResponse = await api.get('/gl_admin/users');
            setUsers(usersResponse.data);
            
            // Then fetch all communities
            const communitiesResponse = await api.get('/gl_admin/communities');
            setCommunities(communitiesResponse.data);
            
            // Instead of fetching communities for each user, we'll use the direct endpoint
            // This is more efficient, but we'll also have a fallback approach if needed
            
            try {
                // Try to use the direct endpoint to get all community-user associations
                const associationsResponse = await api.get('/gl_admin/community-users');
                setAssociations(associationsResponse.data || []);
                
                // If the endpoint isn't available yet, we'll show an error message
                if (!associationsResponse.data || associationsResponse.data.length === 0) {
                    setError("No community-user associations found or endpoint not implemented. Please add the community-users endpoint to your global_admin.py file.");
                }
            } catch (associationsError) {
                setError("Failed to fetch community-user associations. Please implement the /gl_admin/community-users endpoint.");
                
                // Set an empty array if we couldn't get the data
                setAssociations([]);
            }
            
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch associations: ' + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAssociations();
    }, []);

    const handleRoleChange = async (userId, communityId, newRole) => {
        if (!window.confirm(`Are you sure you want to change this user's role?`)) {
            return;
        }
        
        try {
            // Use the community-specific endpoint to update role
            await api.put(`/gl_admin/community-users/${userId}/role`, 
                { community_id: communityId, role: newRole },
                { headers: { 'X-CSRFToken': csrfToken } }
            );
            
            // Update local state
            setAssociations(prevAssociations => 
                prevAssociations.map(assoc => 
                    (assoc.user_id === userId && assoc.community_id === communityId) 
                        ? {...assoc, role: newRole} 
                        : assoc
                )
            );
        } catch (err) {
            setError('Failed to update user role: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleRemoveUser = async (userId, communityId) => {
        if (!window.confirm('Are you sure you want to remove this user from the community?')) {
            return;
        }
        
        try {
            // Use the community-specific endpoint to remove user
            await api.delete(`/gl_admin/community-users/${userId}/${communityId}`, {
                headers: { 'X-CSRFToken': csrfToken }
            });
            
            // Update local state
            setAssociations(prevAssociations => 
                prevAssociations.filter(assoc => 
                    !(assoc.user_id === userId && assoc.community_id === communityId)
                )
            );
        } catch (err) {
            setError('Failed to remove user from community: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleSetPrimary = async (userId, communityId) => {
        if (!window.confirm('Are you sure you want to set this as the user\'s primary community?')) {
            return;
        }
        
        try {
            // Use the user endpoint to set primary community
            await api.put(`/gl_admin/community-users/${userId}/primary`, 
                { community_id: communityId },
                { headers: { 'X-CSRFToken': csrfToken } }
            );
            
            // Update local state
            setAssociations(prevAssociations => 
                prevAssociations.map(assoc => ({
                    ...assoc,
                    is_primary: (assoc.user_id === userId && assoc.community_id === communityId)
                }))
            );
        } catch (err) {
            setError('Failed to set primary community: ' + (err.response?.data?.message || err.message));
        }
    };

    // Filter the associations based on selected filters
    const filteredAssociations = associations.filter(assoc => {
        // Apply community filter
        if (selectedCommunity && assoc.community_id !== parseInt(selectedCommunity)) {
            return false;
        }
        
        // Apply user filter
        if (selectedUser && assoc.user_id !== parseInt(selectedUser)) {
            return false;
        }
        
        // Apply role filter
        if (selectedRole && assoc.role !== selectedRole) {
            return false;
        }
        
        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                assoc.display_name?.toLowerCase().includes(term) ||
                assoc.email?.toLowerCase().includes(term) ||
                assoc.community_name?.toLowerCase().includes(term)
            );
        }
        
        return true;
    });

    // Create statistics
    const stats = {
        totalAssociations: associations.length,
        totalUsers: new Set(associations.map(a => a.user_id)).size,
        totalCommunities: new Set(associations.map(a => a.community_id)).size,
        roleBreakdown: associations.reduce((acc, assoc) => {
            acc[assoc.role] = (acc[assoc.role] || 0) + 1;
            return acc;
        }, {}),
        usersPerCommunity: communities.map(community => {
            const communityUsers = associations.filter(a => a.community_id === community.id);
            return {
                community_id: community.id,
                community_name: community.name,
                users_count: communityUsers.length,
                admin_count: communityUsers.filter(a => a.role === 'community_admin').length
            };
        }),
        communitiesPerUser: users.map(user => {
            const userCommunities = associations.filter(a => a.user_id === user.id);
            return {
                user_id: user.id,
                display_name: user.display_name || user.email?.split('@')[0],
                communities_count: userCommunities.length,
                primary_community: userCommunities.find(a => a.is_primary)?.community_name || 'None'
            };
        })
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Community-User Associations</h2>
                <Button 
                    variant="outline-secondary" 
                    onClick={fetchAssociations}
                >
                    <FontAwesomeIcon icon={faSync} className="me-1" /> Refresh Data
                </Button>
            </div>
            
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Tabs className="mb-4">
                <Tab eventKey="associations" title="Associations">
                    {/* Filters */}
                    <Card className="mb-3">
                        <Card.Header>
                            <FontAwesomeIcon icon={faFilter} className="me-2" />
                            Filters
                        </Card.Header>
                        <Card.Body>
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Community</Form.Label>
                                        <Form.Select
                                            value={selectedCommunity}
                                            onChange={(e) => setSelectedCommunity(e.target.value)}
                                        >
                                            <option value="">All Communities</option>
                                            {communities.map(community => (
                                                <option key={community.id} value={community.id}>
                                                    {community.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>User</Form.Label>
                                        <Form.Select
                                            value={selectedUser}
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                        >
                                            <option value="">All Users</option>
                                            {users.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.display_name || user.email?.split('@')[0]}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Role</Form.Label>
                                        <Form.Select
                                            value={selectedRole}
                                            onChange={(e) => setSelectedRole(e.target.value)}
                                        >
                                            <option value="">All Roles</option>
                                            <option value="community_admin">Community Admin</option>
                                            <option value="member">Member</option>
                                            <option value="user">User</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Search</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FontAwesomeIcon icon={faSearch} />
                                            </span>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search users or communities..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </Form.Group>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                    
                    {loading ? (
                        <div className="text-center mt-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : filteredAssociations.length === 0 ? (
                        <Alert variant="info">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            No associations found with the current filters.
                        </Alert>
                    ) : (
                        <div className="table-responsive">
                            <Table striped hover className="align-middle">
                                <thead>
                                    <tr>
                                        <th>User ID</th>
                                        <th>Display Name</th>
                                        <th>Email</th>
                                        <th>Community ID</th>
                                        <th>Community Name</th>
                                        <th>Role</th>
                                        <th>Primary</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAssociations.map((assoc, index) => (
                                        <tr key={`${assoc.user_id}-${assoc.community_id}`}>
                                            <td>{assoc.user_id}</td>
                                            <td>{assoc.display_name || assoc.email?.split('@')[0]}</td>
                                            <td>{assoc.email}</td>
                                            <td>{assoc.community_id}</td>
                                            <td>{assoc.community_name}</td>
                                            <td>
                                                <Badge 
                                                    bg={assoc.role === 'community_admin' ? 'primary' : 'secondary'}
                                                >
                                                    {assoc.role === 'community_admin' ? 
                                                        <FontAwesomeIcon icon={faUserShield} className="me-1" /> :
                                                        <FontAwesomeIcon icon={faUser} className="me-1" />
                                                    }
                                                    {assoc.role}
                                                </Badge>
                                            </td>
                                            <td>
                                                {assoc.is_primary ? (
                                                    <Badge bg="success">Primary</Badge>
                                                ) : (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline-secondary"
                                                        onClick={() => handleSetPrimary(assoc.user_id, assoc.community_id)}
                                                    >
                                                        Set Primary
                                                    </Button>
                                                )}
                                            </td>
                                            <td>
                                                <div className="btn-group">
                                                    {assoc.role === 'community_admin' ? (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>Set as Member</Tooltip>}
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={() => handleRoleChange(assoc.user_id, assoc.community_id, 'member')}
                                                            >
                                                                <FontAwesomeIcon icon={faUser} />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>Set as Admin</Tooltip>}
                                                        >
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleRoleChange(assoc.user_id, assoc.community_id, 'community_admin')}
                                                            >
                                                                <FontAwesomeIcon icon={faUserShield} />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    )}
                                                    
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>Remove from Community</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveUser(assoc.user_id, assoc.community_id)}
                                                            disabled={assoc.is_primary}
                                                        >
                                                            <FontAwesomeIcon icon={faUserMinus} />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            
                            <div className="text-muted mt-2">
                                Showing {filteredAssociations.length} of {associations.length} total associations
                            </div>
                        </div>
                    )}
                </Tab>
                
                <Tab eventKey="stats" title="Statistics">
                    <div className="row mb-4">
                        <div className="col-md-4">
                            <Card className="h-100">
                                <Card.Header>Overview</Card.Header>
                                <Card.Body>
                                    <div className="mb-3">
                                        <div className="fw-bold">Total Associations:</div>
                                        <div className="fs-3">{stats.totalAssociations}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="fw-bold">Total Users:</div>
                                        <div className="fs-3">{stats.totalUsers}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="fw-bold">Total Communities:</div>
                                        <div className="fs-3">{stats.totalCommunities}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        
                        <div className="col-md-8">
                            <Card className="h-100">
                                <Card.Header>Role Distribution</Card.Header>
                                <Card.Body>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Role</th>
                                                <th>Count</th>
                                                <th>Percentage</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(stats.roleBreakdown).map(([role, count]) => (
                                                <tr key={role}>
                                                    <td>
                                                        <Badge 
                                                            bg={role === 'community_admin' ? 'primary' : 'secondary'}
                                                        >
                                                            {role === 'community_admin' ? 
                                                                <FontAwesomeIcon icon={faUserShield} className="me-1" /> :
                                                                <FontAwesomeIcon icon={faUser} className="me-1" />
                                                            }
                                                            {role}
                                                        </Badge>
                                                    </td>
                                                    <td>{count}</td>
                                                    <td>
                                                        {((count / stats.totalAssociations) * 100).toFixed(1)}%
                                                        <div className="progress mt-1" style={{ height: '5px' }}>
                                                            <div 
                                                                className={`progress-bar ${role === 'community_admin' ? 'bg-primary' : 'bg-secondary'}`}
                                                                style={{ width: `${(count / stats.totalAssociations) * 100}%` }}
                                                            ></div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-6">
                            <Card>
                                <Card.Header>Users per Community</Card.Header>
                                <Card.Body>
                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Community</th>
                                                    <th>Users</th>
                                                    <th>Admins</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.usersPerCommunity
                                                    .sort((a, b) => b.users_count - a.users_count)
                                                    .map(community => (
                                                    <tr key={community.community_id}>
                                                        <td>{community.community_name}</td>
                                                        <td>{community.users_count}</td>
                                                        <td>
                                                            {community.admin_count}
                                                            {community.users_count > 0 && (
                                                                <small className="text-muted ms-1">
                                                                    ({((community.admin_count / community.users_count) * 100).toFixed(0)}%)
                                                                </small>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        
                        <div className="col-md-6">
                            <Card>
                                <Card.Header>Communities per User</Card.Header>
                                <Card.Body>
                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Display Name</th>
                                                    <th>Communities</th>
                                                    <th>Primary Community</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.communitiesPerUser
                                                    .sort((a, b) => b.communities_count - a.communities_count)
                                                    .map(user => (
                                                    <tr key={user.user_id}>
                                                        <td>{user.display_name}</td>
                                                        <td>{user.communities_count}</td>
                                                        <td>{user.primary_community}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
};

export default CommunityUsersAssociations;